import { AuthProvider } from 'react-admin';
import { getApiUrl } from './utils/apiUrl';

const apiUrl = getApiUrl();

// Log the API URL being used (helpful for debugging)
console.log('Admin app API URL:', apiUrl);

export const authProvider: AuthProvider = {
    login: async ({ username, password }) => {
        try {
            console.log('Attempting login to:', `${apiUrl}/api/v1/login`);
            const response = await fetch(`${apiUrl}/api/v1/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: username,
                    password: password,
                }),
            });

            if (response.ok) {
                const data = await response.json();
                localStorage.setItem('admin_token', data.access_token);
                
                // Get user data by making another request
                const userResponse = await fetch(`${apiUrl}/api/v1/test-token`, {
                    headers: {
                        'Authorization': `Bearer ${data.access_token}`,
                    },
                });
                
                if (userResponse.ok) {
                    const userData = await userResponse.json();
                    localStorage.setItem('admin_user', JSON.stringify(userData));
                }
                
                return Promise.resolve();
            } else {
                // Parse error response for better error messages
                let errorData;
                try {
                    errorData = await response.json();
                } catch (e) {
                    // If response isn't JSON, use status text
                    errorData = { detail: response.statusText || 'Unknown error' };
                }
                
                let errorMessage = 'Login failed';
                
                if (errorData.detail) {
                    if (errorData.detail === 'Incorrect email or password') {
                        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
                    } else if (errorData.detail === 'Inactive user') {
                        errorMessage = 'Your account has been deactivated. Please contact support.';
                    } else {
                        errorMessage = `Error: ${errorData.detail}`;
                    }
                } else if (errorData.message) {
                    errorMessage = `Error: ${errorData.message}`;
                } else if (response.status === 400) {
                    errorMessage = `Bad Request (400): ${JSON.stringify(errorData)}`;
                } else if (response.status === 401) {
                    errorMessage = 'Invalid email or password. Please check your credentials and try again.';
                } else if (response.status === 403) {
                    errorMessage = 'Access denied. You do not have permission to access the admin panel.';
                } else if (response.status === 404) {
                    errorMessage = 'Login service not found. Please check your connection and try again.';
                } else if (response.status >= 500) {
                    errorMessage = 'Server error. Please try again later or contact support.';
                }
                
                console.error('Login error:', {
                    status: response.status,
                    statusText: response.statusText,
                    errorData,
                    apiUrl
                });
                
                return Promise.reject(new Error(errorMessage));
            }
        } catch (error) {
            // Handle network errors
            if (error instanceof TypeError && error.message.includes('fetch')) {
                return Promise.reject(new Error('Network error. Please check your internet connection and try again.'));
            }
            
            // Handle other unexpected errors
            return Promise.reject(new Error('An unexpected error occurred. Please try again.'));
        }
    },
    logout: () => {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        return Promise.resolve();
    },
    checkError: (error) => {
        const status = error.status;
        if (status === 401 || status === 403) {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_user');
            return Promise.reject();
        }
        return Promise.resolve();
    },
    checkAuth: () => {
        const token = localStorage.getItem('admin_token');
        if (!token) {
            return Promise.reject();
        }
        return Promise.resolve();
    },
    getPermissions: () => {
        const user = localStorage.getItem('admin_user');
        if (user) {
            const userData = JSON.parse(user);
            return Promise.resolve(userData.role);
        }
        return Promise.resolve();
    },
    getIdentity: () => {
        const user = localStorage.getItem('admin_user');
        if (user) {
            const userData = JSON.parse(user);
            return Promise.resolve({
                id: userData.id,
                fullName: userData.full_name || userData.email,
                avatar: undefined,
            });
        }
        return Promise.reject();
    },
}; 
